/**
 * Utility functions for document management
 */
import { MAX_DOCUMENT_UPLOAD_MB } from '../constants/documentUpload';

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get appropriate icon for file type
 * @param {string} filename - File name
 * @returns {string} Icon name
 */
export const getFileIcon = (filename) => {
    if (!filename) return 'Description';
    
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
        case 'pdf':
            return 'PictureAsPdf';
        case 'doc':
        case 'docx':
            return 'Description';
        case 'xls':
        case 'xlsx':
            return 'TableChart';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return 'Image';
        case 'txt':
            return 'TextFields';
        default:
            return 'Description';
    }
};

/**
 * Check if file type is allowed
 * @param {File} file - File object
 * @param {Array} allowedTypes - Array of allowed file types
 * @returns {boolean} Whether file type is allowed
 */
export const isFileTypeAllowed = (file, allowedTypes = []) => {
    if (!file) return false;
    
    const extension = file.name.split('.').pop()?.toLowerCase();
    return allowedTypes.includes(extension);
};

/**
 * Check if file size is within limit
 * @param {File} file - File object
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {boolean} Whether file size is within limit
 */
export const isFileSizeValid = (file, maxSizeMB = MAX_DOCUMENT_UPLOAD_MB) => {
    if (!file) return false;
    
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
};

/**
 * Get file extension from filename
 * @param {string} filename - File name
 * @returns {string} File extension
 */
export const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Validate file for upload
 * @param {File} file - File object
 * @param {Array} allowedTypes - Array of allowed file types
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {Object} Validation result with isValid and error message
 */
export const validateFile = (file, allowedTypes = [], maxSizeMB = MAX_DOCUMENT_UPLOAD_MB) => {
    if (!file) {
        return { isValid: false, error: 'No file selected' };
    }
    
    if (!isFileTypeAllowed(file, allowedTypes)) {
        return { 
            isValid: false, 
            error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` 
        };
    }
    
    if (!isFileSizeValid(file, maxSizeMB)) {
        return { 
            isValid: false, 
            error: `File size exceeds ${maxSizeMB}MB limit` 
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Generate unique filename
 * @param {string} originalName - Original filename
 * @param {string} prefix - Optional prefix
 * @returns {string} Unique filename
 */
export const generateUniqueFilename = (originalName, prefix = '') => {
    const timestamp = Date.now();
    const extension = getFileExtension(originalName);
    const name = originalName.replace(`.${extension}`, '');
    
    return `${prefix}${name}_${timestamp}.${extension}`;
}; 